import { t } from '../../../shared/config/translations'
import type { ClientRelease } from '../../../shared/types/collection'
import { saveReleaseRating } from '../../api/collection'
import { getSessionSignal } from '../../api/requestSession'
import { updateCachedReleaseByInstanceId } from '../../stores/collectionStore'
import { haptic } from '../../utils/haptics'
import { showSnackbar } from '../Snackbar'
import { emitReleaseUpdated } from './releaseEvents'
import {
	releaseHeaderRatingTemplate,
	releaseRatingStarsTemplate,
} from './releaseRatingTemplates'

const RATING_SAVE_DEBOUNCE_MS = 800

class ReleaseRatingController {
	private readonly sessionSignal = getSessionSignal()
	private confirmedRating: number | null
	private dragMoved = false
	private dragPointerId: number | null = null
	private dragRating: number | null = null
	private dragStartX = 0
	private readonly editorEl: HTMLElement | null
	private readonly release: ClientRelease
	private readonly rootEl: HTMLElement
	private saveInFlight = false
	private saveTimer: number | null = null
	private pendingRating: number | null | undefined
	private suppressNextClick = false
	private readonly toggleEl: HTMLButtonElement | null

	constructor(rootEl: HTMLElement, release: ClientRelease) {
		this.rootEl = rootEl
		this.release = release
		this.confirmedRating = release.rating
		this.editorEl = rootEl.querySelector('#release-rating-editor')
		this.toggleEl = rootEl.querySelector('[data-release-rating-toggle]')

		this.handleClick = this.handleClick.bind(this)
		this.handleDocumentClick = this.handleDocumentClick.bind(this)
		this.handleKeydown = this.handleKeydown.bind(this)
		this.handleOverlayClose = this.handleOverlayClose.bind(this)
		this.handlePointerCancel = this.handlePointerCancel.bind(this)
		this.handlePointerDown = this.handlePointerDown.bind(this)
		this.handlePointerMove = this.handlePointerMove.bind(this)
		this.handlePointerUp = this.handlePointerUp.bind(this)

		rootEl.addEventListener('click', this.handleClick)
		document.addEventListener('click', this.handleDocumentClick, true)
		rootEl.addEventListener('release:details-updated', () => {
			this.renderRating(this.release.rating)
		})
		rootEl.addEventListener('keydown', this.handleKeydown)
		const starsEl = this.starsEl
		starsEl?.addEventListener('pointerdown', this.handlePointerDown)
		starsEl?.addEventListener('pointermove', this.handlePointerMove)
		starsEl?.addEventListener('pointerup', this.handlePointerUp)
		starsEl?.addEventListener('pointercancel', this.handlePointerCancel)
		document.addEventListener(
			'tunetoss:overlay-close',
			this.handleOverlayClose,
			{ once: true },
		)
	}

	private get starsEl(): HTMLElement | null {
		return this.rootEl.querySelector('.release-rating-stars')
	}

	private handleClick(event: Event): void {
		const target = event.target
		if (!(target instanceof Element)) return

		if (target.closest('[data-release-rating-toggle]')) {
			this.setEditorOpen(!!this.editorEl?.hidden)
			return
		}

		const button = target.closest<HTMLButtonElement>(
			'[data-release-rating-value]',
		)
		if (!button) return
		if (this.suppressNextClick) {
			this.suppressNextClick = false
			return
		}

		const buttonRating = Number(button.dataset.releaseRatingValue)
		if (!Number.isInteger(buttonRating)) return
		let selectedRating: number | null = buttonRating
		if (buttonRating === 0) {
			selectedRating = null
		} else if (
			buttonRating === 1 &&
			event instanceof MouseEvent &&
			event.detail > 0
		) {
			selectedRating = this.ratingAt(event.clientX)
		}
		this.setRating(selectedRating)
	}

	private handleKeydown(event: KeyboardEvent): void {
		if (event.key !== 'Escape' || this.editorEl?.hidden) return
		event.preventDefault()
		this.setEditorOpen(false)
		this.toggleEl?.focus()
	}

	private handleDocumentClick(event: MouseEvent): void {
		// Re-rendered or replaced overlay content leaves this controller detached;
		// stop listening then. A pending save still flushes on overlay close.
		if (!this.rootEl.isConnected) {
			document.removeEventListener(
				'click',
				this.handleDocumentClick,
				true,
			)
			return
		}
		if (this.editorEl?.hidden) return
		const target = event.target
		if (
			target instanceof Element &&
			target.closest(
				'[data-release-rating-toggle], .release-rating-stars',
			)
		) {
			return
		}
		this.setEditorOpen(false)
	}

	private handleOverlayClose(): void {
		document.removeEventListener('click', this.handleDocumentClick, true)
		void this.flushSave()
	}

	private handlePointerDown(event: PointerEvent): void {
		if (event.button !== 0 || !this.starsEl) return
		this.dragPointerId = event.pointerId
		this.dragStartX = event.clientX
		this.dragMoved = false
		this.dragRating = this.release.rating
		this.starsEl.setPointerCapture(event.pointerId)
	}

	private handlePointerMove(event: PointerEvent): void {
		if (event.pointerId !== this.dragPointerId) return
		if (Math.abs(event.clientX - this.dragStartX) < 4 && !this.dragMoved) {
			return
		}
		this.dragMoved = true
		event.preventDefault()
		const rating = this.ratingAt(event.clientX)
		// Tick once per star crossed, like a native picker.
		if (rating !== this.dragRating) {
			this.dragRating = rating
			void haptic('select')
		}
		this.renderRating(rating)
	}

	private handlePointerUp(event: PointerEvent): void {
		if (event.pointerId !== this.dragPointerId) return
		this.releasePointer(event.pointerId)
		if (!this.dragMoved) {
			this.setRating(this.ratingAt(event.clientX))
			return
		}
		event.preventDefault()
		this.suppressNextClick = true
		window.setTimeout(() => {
			this.suppressNextClick = false
		}, 0)
		this.setRating(this.ratingAt(event.clientX))
	}

	private handlePointerCancel(event: PointerEvent): void {
		if (event.pointerId !== this.dragPointerId) return
		this.releasePointer(event.pointerId)
		this.renderRating(this.release.rating)
	}

	private releasePointer(pointerId: number): void {
		if (this.starsEl?.hasPointerCapture(pointerId)) {
			this.starsEl.releasePointerCapture(pointerId)
		}
		this.dragPointerId = null
	}

	private ratingAt(clientX: number): number | null {
		const starElements = this.starsEl?.querySelectorAll<HTMLElement>(
			'.release-rating-star',
		)
		const firstStarRect = starElements?.[0]?.getBoundingClientRect()
		const lastStarRect = starElements?.[4]?.getBoundingClientRect()
		if (!firstStarRect?.width || !lastStarRect) return this.release.rating
		if (clientX < firstStarRect.left) return null
		const width = lastStarRect.right - firstStarRect.left
		const position = Math.max(
			0,
			Math.min(width - 1, clientX - firstStarRect.left),
		)
		return Math.floor((position / width) * 5) + 1
	}

	private setEditorOpen(isOpen: boolean): void {
		if (!this.editorEl || !this.toggleEl) return
		this.editorEl.hidden = !isOpen
		this.rootEl
			.querySelector('.cover-wrapper')
			?.classList.toggle('is-rating-editing', isOpen)
		this.toggleEl.setAttribute('aria-expanded', String(isOpen))
		this.toggleEl.setAttribute(
			'aria-label',
			t(isOpen ? 'release.closeRatingEditor' : 'release.editRating'),
		)
	}

	private setRating(rating: number | null): void {
		if (this.sessionSignal.aborted || rating === this.release.rating) return
		// Dragging already ticked for every star on the way.
		if (rating !== this.dragRating) void haptic('select')
		this.dragRating = rating
		this.applyRatingLocally(rating)
		this.pendingRating = rating
		this.scheduleSave()
	}

	private applyRatingLocally(rating: number | null): void {
		const changed = this.release.rating !== rating
		this.release.rating = rating
		this.renderRating(rating)
		updateCachedReleaseByInstanceId(this.release)
		if (changed) {
			emitReleaseUpdated(this.release, 'rating', false)
		}
	}

	private renderRating(rating: number | null): void {
		const headerEl = this.rootEl.querySelector<HTMLElement>(
			'[data-release-header-rating]',
		)
		if (headerEl) {
			const yearIsVisible = !!this.rootEl
				.querySelector('[data-release-year]')
				?.textContent?.trim()
			headerEl.innerHTML = releaseHeaderRatingTemplate(
				rating,
				yearIsVisible,
			)
			headerEl.hidden = rating === null
		}
		if (this.starsEl) {
			this.starsEl.innerHTML = releaseRatingStarsTemplate(rating)
		}
	}

	private scheduleSave(delayMs = RATING_SAVE_DEBOUNCE_MS): void {
		if (this.saveTimer !== null) window.clearTimeout(this.saveTimer)
		this.saveTimer = window.setTimeout(() => {
			this.saveTimer = null
			void this.flushSave()
		}, delayMs)
	}

	private async flushSave(): Promise<void> {
		if (this.saveTimer !== null) window.clearTimeout(this.saveTimer)
		this.saveTimer = null
		if (this.sessionSignal.aborted) return
		if (this.saveInFlight || this.pendingRating === undefined) return
		const sentRating = this.pendingRating
		this.pendingRating = undefined
		this.saveInFlight = true

		try {
			const response = await saveReleaseRating(
				this.release.data.id,
				sentRating,
			)
			if (this.sessionSignal.aborted) return
			this.confirmedRating = response.rating
			if (this.pendingRating === undefined) {
				this.applyRatingLocally(response.rating)
			}
			// A newer local edit may still be pending; publish only the saved value.
			emitReleaseUpdated(
				{ ...this.release, rating: response.rating },
				'rating',
			)
		} catch (error) {
			if (this.sessionSignal.aborted) return
			console.error('Failed to save Discogs release rating', error)
			if (this.pendingRating === undefined) {
				this.applyRatingLocally(this.confirmedRating)
				showSnackbar(t('release.ratingSaveFailed'), 'error')
			}
		} finally {
			this.saveInFlight = false
			if (!this.sessionSignal.aborted && this.pendingRating !== undefined)
				this.scheduleSave(0)
		}
	}
}

export function initializeReleaseRating(
	releaseEl: HTMLElement,
	release: ClientRelease,
): void {
	new ReleaseRatingController(releaseEl, release)
}
